import { useCallback, useEffect, useMemo, useState } from "react";
import { getTimeRange } from "../../Dashboard/Exchange3/Chart";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { BrushStart } from "../../../models/chart_model";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useDispatch, useSelector } from "react-redux";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { H_CHART_TYPES } from "../../Hppc/const";
import {
  getChhattisgarhDiscomData,
  getChhattisgarhForecastData,
  getTrendMetrics,
} from "../Utility/RestApi";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { formatDateForAPI } from "../const";
import MetricChip, { MetricChipHead } from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { TrendData } from "../../../models/metrics";
// ─── Peak & Summary sub-views ────────────────────────────────────────────────
import { ChhattisgarhPeakAnalysis } from "./Forecast_LoadPeak";
import { ChhattisgarhSummaryAnalysis } from "./Forecast_LoadSummary";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

// ─── Intervals ───────────────────────────────────────────────────────────────
const Intervals = [
  {
    label: "Time Slot",
    key: "slot",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const DISCOM_OPTIONS = ["CSPDCL"];
const UNITS = ["MW", "MWh", "LU"];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

// ─── Chart suffixes — visible in Type filter ─────────────────────────────────
const CHART_SUFFIXES = [
  { suffix: "demand", displayName: "Actual Load", stackSuffix: "demand" },
  {
    suffix: "demand_forecast_d1",
    displayName: "Forecast D+1",
    stackSuffix: "forecast_d1",
  },
  {
    suffix: "demand_forecast_d2",
    displayName: "Forecast D+2",
    stackSuffix: "forecast_d2",
  },
  {
    suffix: "deviation",
    displayName: "Deviation %",
    stackSuffix: "deviation",
  },
];

// Internal suffixes that get enabled when "Deviation %" is selected
const DEVIATION_SUFFIXES = [
  {
    suffix: "deviation_d1",
    displayName: "(Forecast D+1) Deviation %",
    stackSuffix: "deviation_d1",
  },
  {
    suffix: "deviation_d2",
    displayName: "(Forecast D+2) Deviation %",
    stackSuffix: "deviation_d2",
  },
];

// ─── Helper ───────────────────────────────────────────────────────────────────
function getMonthWithYear(dateString: string): string {
  const date = new Date(dateString);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  return `${month}-${year}`;
}

// ─── Component ────────────────────────────────────────────────────────────────
export function ChhattisgarhForecast() {
  const dispatch = useDispatch();
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  // ── State ─────────────────────────────────────────────────────────────────
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [selectedDiscom, setSelectedDiscom] = useState<string[]>([]);
  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }
  const [data, setData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<TrendData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const [selectedSuffix, setSelectedSuffix] = useState(
    CHART_SUFFIXES.filter((s) =>
      ["demand", "demand_forecast_d1", "demand_forecast_d2", "deviation"].includes(s.suffix),
    ),
  );

  // True when D+2 forecast has at least one non-zero value in the fetched data
  const hasD2Data = useMemo(
    () => data.some((item) => (item.demand_forecast_d2_mw || 0) !== 0),
    [data],
  );

  // Expand "deviation" into d1/d2 based on which forecasts are selected
  // Sort order: Actual Load → Forecasts → Deviations
  const SUFFIX_ORDER: Record<string, number> = {
    demand: 0,
    demand_forecast_d1: 1,
    demand_forecast_d2: 2,
    deviation_d1: 3,
    deviation_d2: 4,
  };

  const expandedSuffix = useMemo(() => {
    const hasD1 = selectedSuffix.some((s) => s.suffix === "demand_forecast_d1");
    const hasD2 = selectedSuffix.some((s) => s.suffix === "demand_forecast_d2");
    const result: typeof CHART_SUFFIXES = [];
    selectedSuffix.forEach((s) => {
      if (s.suffix === "deviation") {
        if (hasD1) result.push(DEVIATION_SUFFIXES[0]);
        if (hasD2 && hasD2Data) result.push(DEVIATION_SUFFIXES[1]);
      } else if (s.suffix === "demand_forecast_d2" && !hasD2Data) {
        // Skip D+2 forecast when no D+2 data exists
      } else {
        result.push(s);
      }
    });
    result.sort(
      (a, b) => (SUFFIX_ORDER[a.suffix] ?? 99) - (SUFFIX_ORDER[b.suffix] ?? 99),
    );
    return result;
  }, [selectedSuffix, hasD2Data]);

  // ── Chart object builder ──────────────────────────────────────────────────
  const DEVIATION_COLORS = [
    "#00c8b4",
    "#ff69b4",
    "#adff2f",
    "#e040fb",
    "#ffdd00",
    "#40c4ff",
  ];

  const D2_COLORS = ["#ab47bc", "#26c6da", "#d4e157", "#ffa726", "#ec407a"];

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    _interval: { isTime: boolean },
    _chartType: { value: string; stackId?: string },
  ) => {
    const unit = selectedUnit.toLowerCase();
    const isDeviation = suffix === "deviation_d1" || suffix === "deviation_d2";
    const deviationType = suffix === "deviation_d1" ? "d1" : "d2";

    const baseColor = ExchangeColors[index % ExchangeColors.length];
    const color =
      suffix === "demand"
        ? baseColor
        : suffix === "demand_forecast_d1"
          ? lightenColor(baseColor, 30)
          : suffix === "demand_forecast_d2"
            ? D2_COLORS[index % D2_COLORS.length]
            : suffix === "deviation_d1"
              ? DEVIATION_COLORS[index % DEVIATION_COLORS.length]
              : suffix === "deviation_d2"
                ? DEVIATION_COLORS[(index % DEVIATION_COLORS.length) + 1]
                : "#ff9f40";

    return {
      dataKey: `${key}_${suffix}_${unit}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`.trim(),
      yAxisId: isDeviation ? "right" : undefined,
      legendColor: color,
      stroke: color,
      type: _chartType.value,
      stackId:
        !isDeviation && _chartType.stackId !== undefined
          ? stackSuffix
          : undefined,
      ...(isDeviation && {
        unit: selectedUnit,
        deviationValueKey: `${key}_deviation_${deviationType}_value_${unit}`,
      }),
    };
  };

  // ── Fetch data — single defaultRangeDate ──────────────────────────────────
  const _handlefetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDate = formatDateForAPI(dates.startDate);
      const endDate = formatDateForAPI(dates.endDate);

      const [actualItems, forecastItems] = await Promise.all([
        getChhattisgarhDiscomData(startDate, endDate),
        getChhattisgarhForecastData(startDate, endDate),
      ]);

      // ── Same pattern as TrendAnalysis: keep actual and forecast as separate
      // rows, then append into one flat array. FormatChartData groups by
      // date+block and sums all rows sharing that key, so each field gets
      // its contribution independently — forecast shows even when actual is
      // empty (future dates) and vice versa.

      // Actual rows — forecast fields zero-filled.
      // month/dateIndex/dates are already set by the RestApi mapper via ...item
      const actualRows = actualItems.map((item: any) => ({
        ...item,
        demand_forecast_d1_mw: 0,
        demand_forecast_d1_mwh: 0,
        demand_forecast_d1_lu: 0,
        demand_forecast_d2_mw: 0,
        demand_forecast_d2_mwh: 0,
        demand_forecast_d2_lu: 0,
      }));

      // Forecast rows — actual fields zero-filled; D+1 and D+2 split by forecast_type.
      // month/dateIndex/dates are already set by the RestApi mapper via ...item
      const forecastRows = forecastItems.map((item: any) => {
        const fType = item.forecast_type || "T+1";
        const isD1 = fType === "T+1";
        const val = item.demand_forecast_mw || 0;
        return {
          ...item,
          // Actual fields are zero — this row carries only forecast values
          demand_mw: 0,
          demand_mwh: 0,
          demand_lu: 0,
          demand_forecast_d1_mw: isD1 ? val : 0,
          demand_forecast_d1_mwh: isD1 ? val / 4 : 0,
          demand_forecast_d1_lu: isD1 ? val / 4 / 100 : 0,
          demand_forecast_d2_mw: isD1 ? 0 : val,
          demand_forecast_d2_mwh: isD1 ? 0 : val / 4,
          demand_forecast_d2_lu: isD1 ? 0 : val / 4 / 100,
        };
      });

      // Deduplicate: one actual row and one D+1/D+2 row per date+block is enough
      const seen = new Set<string>();
      const deduped = [...actualRows, ...forecastRows].filter((item) => {
        const k = `${item.date}_${item.block}_${item.demand_forecast_d1_mw}_${item.demand_forecast_d2_mw}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });

      setData(deduped);
    } catch (err) {
      console.error("Error in data fetching:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  // ── Fetch metrics ─────────────────────────────────────────────────────────
  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      setMetrics(null);
      setMetricsLoading(false);
      return;
    }
    setMetricsLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const ranges = [
        {
          startDate: formatDateForAPI(dates.startDate),
          endDate: formatDateForAPI(dates.endDate),
          blocks: selectedBlock.map((block: any) => block.value),
        },
      ];
      // Send only forecast types — same pattern as TrendAnalysis
      const selectedTypes = selectedSuffix
        .map((s) => s.displayName)
        .filter((s) => s === "Forecast D+1");
      const metricsData = await getTrendMetrics({
        ranges,
        aggregated: true,
        selectedTypes,
      });
      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedBlock, selectedSuffix]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // ── Format chart data ─────────────────────────────────────────────────────
  useEffect(() => {
    let filteredData = [...data];

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((o) => o.value).includes(item.block),
      );
    }

    filteredData.sort((a, b) => {
      const d = new Date(a.date).getTime() - new Date(b.date).getTime();
      return d !== 0 ? d : a.block - b.block;
    });

    function getKeys() {
      if (selectedInterval.key === "slot") return ["date", "block"];
      if (selectedInterval.key === "date") return ["date"];
      return ["month"];
    }

    const aggregationKeys = [
      "demand_mw",
      "demand_mwh",
      "demand_lu",
      "demand_forecast_d1_mw",
      "demand_forecast_d1_mwh",
      "demand_forecast_d1_lu",
      "demand_forecast_d2_mw",
      "demand_forecast_d2_mwh",
      "demand_forecast_d2_lu",
    ];

    const par: ChartDataParams = {
      keys: getKeys(),
      groupBy: [],
      tableKeys: ["date", "block", "month"],
      volumeKey: "",
      aggregationKeys,
      averageKeys: aggregationKeys,
      averageDuplicationKeys: ["date", "block", "month"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);

    const unit = selectedUnit.toLowerCase();

    // Compute deviation % for D+1 and D+2
    const processedData = cData.data.map((dataPoint) => {
      const newDataPoint = { ...dataPoint };
      cData.keys.forEach((key) => {
        const actualVal = dataPoint[`${key}_demand_${unit}`] ?? 0;
        const d1Val = dataPoint[`${key}_demand_forecast_d1_${unit}`] ?? 0;
        const d2Val = dataPoint[`${key}_demand_forecast_d2_${unit}`] ?? 0;

        // Null out zero forecast values so the chart line is not drawn
        if (d1Val === 0)
          newDataPoint[`${key}_demand_forecast_d1_${unit}`] = null;
        if (d2Val === 0)
          newDataPoint[`${key}_demand_forecast_d2_${unit}`] = null;

        // D+1 deviation
        if (actualVal !== 0 && d1Val !== 0) {
          newDataPoint[`${key}_deviation_d1_${unit}`] =
            (Math.abs(actualVal - d1Val) / actualVal) * 100;
          newDataPoint[`${key}_deviation_d1_value_${unit}`] = Math.abs(
            actualVal - d1Val,
          );
        } else {
          newDataPoint[`${key}_deviation_d1_${unit}`] = null;
          newDataPoint[`${key}_deviation_d1_value_${unit}`] = null;
        }

        // D+2 deviation
        if (actualVal !== 0 && d2Val !== 0) {
          newDataPoint[`${key}_deviation_d2_${unit}`] =
            (Math.abs(actualVal - d2Val) / actualVal) * 100;
          newDataPoint[`${key}_deviation_d2_value_${unit}`] = Math.abs(
            actualVal - d2Val,
          );
        } else {
          newDataPoint[`${key}_deviation_d2_${unit}`] = null;
          newDataPoint[`${key}_deviation_d2_value_${unit}`] = null;
        }
      });
      return newDataPoint;
    });

    setChartData(processedData);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [data, selectedInterval, selectedBlock, selectedSuffix, selectedUnit]);

  // ── Legends ───────────────────────────────────────────────────────────────
  const legends = useMemo(
    () =>
      expandedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
        chartKeys.map((key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedInterval,
            selectedChartType as any,
          ),
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      expandedSuffix,
      chartKeys,
      selectedInterval,
      selectedChartType,
      selectedUnit,
    ],
  );

  // Preserve legend order from expandedSuffix (Actual → Forecast → Deviation)
  const customLegendOrder = useMemo(
    () => legends.map((l) => l.name),
    [legends],
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  function handleXAxisModeChange(_item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleUnitChange(unit: string) {
    setSelectedUnit(unit);
  }

  function handleTypeChange(selected: string[]) {
    setSelectedSuffix(
      CHART_SUFFIXES.filter((s) => selected.includes(s.displayName)),
    );
    dispatch(setLoadingFilterName(""));
  }

  function getSelectedTypes() {
    return selectedSuffix.map((s) => s.displayName);
  }

  const xDataKey = useMemo(() => {
    if (selectedInterval.isTime) return "block";
    return selectedInterval.values[0];
  }, [selectedInterval]);

  const secondXDataKey = useMemo(() => {
    if (selectedInterval.isTime) return "date";
    return undefined;
  }, [selectedInterval.isTime]);

  // ── Custom tooltip ────────────────────────────────────────────────────────
  const customTooltip = useCallback(
    (params: any) => {
      const items: any[] = Array.isArray(params) ? params : [params];
      const param = items[0];
      const dataPoint = chartData[param.dataIndex] || {};
      const unit = selectedUnit.toLowerCase();

      let title = param.name;
      if (secondXDataKey && dataPoint[secondXDataKey]) {
        title = selectedInterval.isTime
          ? `${dataPoint[secondXDataKey]} - ${param.name} (${getTimeRange(parseInt(param.name))})`
          : `${dataPoint[secondXDataKey]} - ${param.name}`;
      } else if (selectedInterval.isTime) {
        title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
      }

      const validItems = items.filter((item) => {
        if (!item.seriesName || item.seriesName.startsWith("__")) return false;
        if (item.value === null || item.value === undefined) return false;
        const v = parseFloat(item.value);
        return !isNaN(v) && isFinite(v);
      });

      // Sort: Actual Load → Forecasts → Deviations
      const tooltipOrder = (name: string) => {
        if (name.includes("Deviation")) return 3;
        if (name.includes("Forecast D+2")) return 2;
        if (name.includes("Forecast D+1")) return 1;
        return 0; // Actual Load
      };
      validItems.sort(
        (a, b) => tooltipOrder(a.seriesName) - tooltipOrder(b.seriesName),
      );

      const tooltipItems = validItems.map((item) => {
        const seriesName = item.seriesName as string;
        const isDeviationD1 = seriesName.includes("(Forecast D+1) Deviation %");
        const isDeviationD2 = seriesName.includes("(Forecast D+2) Deviation %");
        const isDeviation = isDeviationD1 || isDeviationD2;
        const numVal = parseFloat(item.value);
        const formattedVal = numVal
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

        let displayValue: string;
        if (isDeviation) {
          const dType = isDeviationD1 ? "d1" : "d2";
          // Find the matching chart key to look up the MW value
          const devValueKey =
            chartKeys.length > 0
              ? `${chartKeys[0]}_deviation_${dType}_value_${unit}`
              : "";
          const devVal = dataPoint[devValueKey];
          if (devVal !== null && devVal !== undefined) {
            const devFormatted = Math.abs(parseFloat(devVal))
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            displayValue = `(${devFormatted} ${selectedUnit}) ${formattedVal} %`;
          } else {
            displayValue = `${formattedVal} %`;
          }
        } else {
          displayValue = `${formattedVal} ${selectedUnit}`;
        }

        return `
    <div class="tooltip-item" data-name="${seriesName.toLowerCase()}">
      <div class="color-indicator" style="background-color: ${item.color}"></div>
      <div class="series-name">${seriesName}</div>
      <div class="value">${displayValue}</div>
    </div>`;
      });

      return `<div class="enhanced-tooltip"><div class="tooltip-header"><div class="tooltip-title">${title}</div></div><div class="tooltip-content">${tooltipItems.join("")}</div></div><style>.enhanced-tooltip{font-family:'Inter',system-ui,sans-serif;width:100%;min-width:200px}.tooltip-header{display:flex;flex-direction:column;gap:8px;border-bottom:1px solid #eaeaea;padding-bottom:8px;margin-bottom:8px}.tooltip-title{font-weight:600;font-size:13px;color:#333}.tooltip-content{max-height:300px;overflow-y:auto;padding-right:10px}.tooltip-item{display:flex;align-items:center;padding:4px 0;border-bottom:1px solid #f0f0f0}.tooltip-item:last-child{border-bottom:none}.color-indicator{width:10px;height:10px;border-radius:50%;margin-right:8px}.series-name{flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:12px}.value{font-weight:500;margin-left:10px;font-size:12px}</style>`;
    },
    [chartData, chartKeys, selectedUnit, secondXDataKey, selectedInterval],
  );

  const summaryExtraKeys = selectedInterval.isTime
    ? ["block", selectedInterval.values[1]]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.values.map((value) =>
    value === "date" ? "Date" : value,
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends as any);

  const commonSummaryProps = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "CSPDCL Forecast Load",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  // ── Props forwarded to Peak / Summary sub-views ───────────────────────────
  const sharedSubViewProps = {
    onXAxisModeChange: handleXAxisModeChange,
    selectedUnit,
    onUnitChange: handleUnitChange,
    selectedDiscom,
    onDiscomChange: handleDiscomChange,
    selectedGroupKeys: "Discom Load",
    onGroupByChange: () => {},
    selectedSuffix,
    onTypeChange: handleTypeChange,
    chartSuffixes: CHART_SUFFIXES,
    metrics,
  };

  // ── Peak / Summary routes ─────────────────────────────────────────────────
  if (selectedXAxisMode.value === "peak") {
    return <ChhattisgarhPeakAnalysis {...sharedSubViewProps} />;
  }

  if (selectedXAxisMode.value === "summary") {
    return <ChhattisgarhSummaryAnalysis {...sharedSubViewProps} />;
  }

  // ── Main Intervals render ─────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((i) => i.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />

          <FilterMenu
            title="Intervals"
            items={Intervals.map((i) => i.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(_item, index) => setSelectedInterval(Intervals[index])}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => handleUnitChange(item)}
          />

          {/* <FilterMenu
            title="Discom"
            items={DISCOM_OPTIONS}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={handleDiscomChange}
          /> */}

          <FilterMenu
            title="Type"
            items={CHART_SUFFIXES.map((s) => s.displayName)}
            selectedItems={getSelectedTypes()}
            setSelectedItems={handleTypeChange}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((i) => i.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Forecast Discom Load"
              subtitle="Fetching Chhattisgarh forecast discom load data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading">
                  Forecast Discom Load ({selectedUnit})
                </h2>

                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((i) => i.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart")
                        downloadChartTableCSV(commonSummaryProps);
                      if (dataTab.label === "Summary")
                        downloadSummaryCSV(commonSummaryProps);
                      if (dataTab.label === "Table")
                        downloadChartTableCSV(commonSummaryProps);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  onlyTitle={true}
                  barCategoryGap={10}
                  secondYAxisDomain={[0, 100]}
                  secondYAxisLabel={
                    selectedSuffix.some((s) => s.suffix === "deviation")
                      ? "Deviation %"
                      : undefined
                  }
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends as any}
                  defaultSelectedLegends={selectedLegends}
                  onLegendSelect={handleLegendSelect}
                  key={`${selectedUnit}-${selectedInterval.key}-${selectedChartType.value}`}
                  xDataKey={xDataKey}
                  secondXDataKey={secondXDataKey}
                  xTick={
                    selectedInterval.isTime
                      ? (value: any) => renderHourTick(value, 10)
                      : undefined
                  }
                  customTooltip={customTooltip}
                  customLegendOrder={customLegendOrder}
                />
              )}

              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...commonSummaryProps} />
              )}

              {dataTab.label === "Table" && (
                <ChartTable {...commonSummaryProps} />
              )}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>CSPDCL</p>}
              text="
    1. Actual Load data is sourced from CSPDCL's official records.
    2. Forecast Load data is provided by GNA Energy."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
